import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import { videos, categories } from '@/mocks/videos';
import type { VideoItem } from '@/mocks/videos';

function VideoCard({ video }: { video: VideoItem }) {
  const navigate = useNavigate();
  return (
    <a
      onClick={(e) => { e.preventDefault(); navigate(`/video/${video.id}`); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
      href={`/video/${video.id}`}
      className="group cursor-pointer"
    >
      <div className="relative aspect-[2/3] rounded-lg overflow-hidden mb-2.5">
        <img
          src={video.coverImage}
          alt={video.title}
          title={`${video.title} - 51影视免费在线观看`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
          loading="lazy"
        />
        <div className="absolute top-2 left-2">
          <span className="px-2 py-0.5 bg-primary-500 text-white text-xs rounded-md font-medium">
            {video.rating}
          </span>
        </div>
        <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent p-3">
          <p className="text-xs text-white/80 font-medium">{video.duration}</p>
        </div>
      </div>
      <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
        {video.title}
      </h3>
      <div className="flex items-center gap-2 mt-1 flex-wrap">
        {video.tags.slice(0, 2).map((tag) => (
          <span key={tag} className="text-xs px-1.5 py-0.5 bg-secondary-100 text-secondary-700 rounded">
            {tag}
          </span>
        ))}
        <span className="text-xs text-foreground-400">{(video.views / 10000).toFixed(0)}万播放</span>
      </div>
    </a>
  );
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState<string>('全部');

  useEffect(() => {
    if (slug && slug !== 'all') {
      const decoded = decodeURIComponent(slug);
      if (categories.includes(decoded)) {
        setActiveCategory(decoded);
      } else {
        setActiveCategory('全部');
      }
    } else {
      setActiveCategory('全部');
    }
  }, [slug]);

  const filteredVideos = activeCategory === '全部'
    ? videos
    : videos.filter((v) => v.category === activeCategory);

  const handleCategoryChange = (cat: string) => {
    setActiveCategory(cat);
    const path = cat === '全部' ? '/category/all' : `/category/${cat}`;
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const breadcrumbStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.51yingshi.com/' },
      { '@type': 'ListItem', position: 2, name: '分类浏览', item: 'https://www.51yingshi.com/category/all' },
      ...(activeCategory !== '全部' ? [{ '@type': 'ListItem', position: 3, name: activeCategory }] : []),
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbStructuredData) }}
      />
      <Navbar />
      <main className="pt-16">
        <div className="w-full px-4 md:px-6 lg:px-8 py-8">
          <div className="max-w-[1280px] mx-auto">
            <nav className="flex items-center gap-1.5 text-xs text-foreground-500 mb-6" aria-label="面包屑导航">
              <a href="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</a>
              <i className="ri-arrow-right-s-line text-foreground-400"></i>
              <span className="text-foreground-700 font-medium">
                {activeCategory === '全部' ? '全部分类' : activeCategory}
              </span>
            </nav>

            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-6">
              {activeCategory === '全部' ? '全部分类' : activeCategory}
            </h1>

            <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 flex-wrap">
              {categories.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => handleCategoryChange(cat)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeCategory === cat
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4" data-product-shop="">
              {filteredVideos.length === 0 ? (
                <div className="col-span-full flex flex-col items-center justify-center py-20 text-center">
                  <div className="w-16 h-16 flex items-center justify-center mb-4">
                    <i className="ri-movie-line text-4xl text-foreground-300"></i>
                  </div>
                  <p className="text-foreground-500 text-sm">该分类暂无内容，请查看其他分类</p>
                </div>
              ) : (
                filteredVideos.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}